import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import HeroBanner from "../components/HeroBanner";
import MovieRow from "../components/MovieRow";
import MovieRowSkeleton from "../components/MovieRowSkeleton";
import ContinueWatchingRow from "../components/ContinueWatchingRow";
import TopTenRow from "../components/TopTenRow";
import OnNowRow from "../components/OnNowRow";
import Footer from "../components/Footer";

export default function Browse() {
  const [sp] = useSearchParams();
  const typeFilter = sp.get("type");
  const { profile } = useAuth();
  const [videos, setVideos] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [genres, setGenres] = useState([]);
  const [continueList, setContinueList] = useState([]);
  const [recRails, setRecRails] = useState([]);
  const [topTen, setTopTen] = useState([]);
  const [onNow, setOnNow] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const [v, f, g, t, n] = await Promise.all([
        api.get("/videos", { params: { limit: 100 } }),
        api.get("/videos/featured"),
        api.get("/videos/genres"),
        api.get("/videos/top10"),
        api.get("/gcr/now-rail").catch(() => ({ data: [] })),
      ]);
      if (cancelled) return;
      setVideos(v.data); setFeatured(f.data); setGenres(g.data); setTopTen(t.data || []); setOnNow(n.data || []);
      setLoading(false);
      if (profile) {
        try {
          const c = await api.get("/playback/continue", { params: { profile_id: profile.profile_id } });
          if (!cancelled) setContinueList(c.data || []);
        } catch (e) {
          console.warn("[browse] continue watching fetch failed", e?.response?.status);
        }
        try {
          const r = await api.get("/recommendations", { params: { profile_id: profile.profile_id } });
          if (!cancelled) setRecRails(r.data || []);
        } catch (e) {
          console.warn("[browse] recommendations fetch failed", e?.response?.status);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [profile]);

  const filtered = useMemo(() => {
    if (!typeFilter) return videos;
    return videos.filter((v) => v.type === typeFilter);
  }, [videos, typeFilter]);

  const hero = featured[Math.floor(Math.random() * (featured.length || 1))] || videos[0];
  const trending = [...filtered].sort((a, b) => b.tmdb_rating - a.tmdb_rating).slice(0, 12);
  const newReleases = [...filtered].sort((a, b) => b.year - a.year).slice(0, 12);

  // Cap the stagger delay class at 6 so deeply-nested genre rails don't load
  // visibly later than the page itself.
  const delayClass = (i) => `delay-${Math.min(i + 1, 6)}`;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar transparent />
      <HeroBanner video={hero} />
      <div className="relative z-10 -mt-20 pb-20">
        {loading ? (
          <>
            <MovieRowSkeleton title="Trending Now" />
            <MovieRowSkeleton title="New Releases" />
            <MovieRowSkeleton />
          </>
        ) : (
          <>
            <div className={`stagger-rail ${delayClass(0)}`}>
              <ContinueWatchingRow items={continueList} />
            </div>
            <div className={`stagger-rail ${delayClass(1)}`}>
              <OnNowRow items={onNow} />
            </div>
            <div className={`stagger-rail ${delayClass(2)}`}>
              <TopTenRow items={topTen} />
            </div>
            <div className={`stagger-rail ${delayClass(3)}`}>
              <MovieRow title="Trending Now" videos={trending} />
            </div>
            {recRails.map((r, idx) => (
              <div key={`rec-${r.anchor.id}`} className={`stagger-rail ${delayClass(4 + idx)}`}>
                <MovieRow title={`Because you watched ${r.anchor.title}`} videos={r.items} />
              </div>
            ))}
            <div className={`stagger-rail ${delayClass(5)}`}>
              <MovieRow title="New Releases" videos={newReleases} />
            </div>
            {genres.map((g, idx) => {
              const items = filtered.filter((v) => v.genres?.includes(g));
              if (items.length < 2) return null;
              return (
                <div key={g} className={`stagger-rail ${delayClass(6 + idx)}`}>
                  <MovieRow title={g} videos={items} />
                </div>
              );
            })}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
